import { inject, Injectable, NgZone } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ExperimentFlag, ExperimentFlags, FeatureFlagService } from '@rma/dashboard/generic/util/feature-flags';
import { HeapService } from '@rma/dashboard/generic/util/heap';
import { rmaShareReplay } from '@rma/generic/rxjs/operators/rma-share-replay';
import { combineLatest, distinctUntilChanged, map, Observable } from 'rxjs';

interface Experiment {
  experiment: ExperimentFlag;
  treatment: unknown;
}

type Experiments = Record<ExperimentFlag, unknown>;

// SAFETY(legacy): unreviewed, pre-dates this rule.
const EMPTY_EXPERIMENT_SET = {} as Experiments;

@Injectable({ providedIn: 'root' })
export class RmaExperimentService {
  private readonly featuresService = inject(FeatureFlagService);
  private readonly heapService = inject(HeapService);

  private readonly zone = inject(NgZone);

  private experiments$!: Observable<Experiments>;
  private snapshot: Experiments = EMPTY_EXPERIMENT_SET;

  public constructor() {
    const experiments = Object.values(ExperimentFlags).map((experiment) => {
      return this.featuresService.getLiveFeature$(experiment, '').pipe(map((treatment): Experiment => ({ experiment, treatment })));
    });

    this.zone.runOutsideAngular(() => {
      this.experiments$ = combineLatest(experiments).pipe(
        takeUntilDestroyed(),
        map((exp) => exp.reduce((accum, curr) => ({ ...accum, [curr.experiment]: curr.treatment }), EMPTY_EXPERIMENT_SET)),
        rmaShareReplay(),
      );

      this.experiments$.pipe(takeUntilDestroyed()).subscribe((exp) => {
        this.snapshot = exp;
        this.heapService.addUserProperties(this.mapToHeapProperties(exp));
      });
    });
  }

  public getExperimentTreatment$<T>(name: ExperimentFlag): Observable<T> {
    return this.experiments$.pipe(
      map((e) => {
        if (!Object.hasOwn(e, name)) {
          throw new Error(`Experiment ${name} has not been defined`);
        }
        const experiment = e[name];
        // SAFETY(legacy): unreviewed, pre-dates this rule.
        return experiment as T;
      }),
      distinctUntilChanged(),
    );
  }

  public getExperimentTreatmentSnapshot<T>(name: ExperimentFlag): T {
    if (!Object.hasOwn(this.snapshot, name)) {
      throw new Error(`Experiment ${name} has not been defined`);
    }
    const experiment = this.snapshot[name];
    // SAFETY(legacy): unreviewed, pre-dates this rule.
    return experiment as T;
  }

  private mapToHeapProperties(obj: Record<ExperimentFlag, unknown>): Record<string, string> {
    const formattedEntries = Object.entries(obj).map(([key, value]) => {
      const formattedKey = key
        .toLowerCase()
        .replace(/experiment\./g, 'rma_exp_')
        .replace(/ /g, '_');
      return [formattedKey, value];
    });
    return Object.fromEntries(formattedEntries);
  }
}
